import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  Box,
  Button,
  Checkbox,
  Divider,
  FormControl,
  FormControlLabel,
  Grid,
  IconButton,
  MenuItem,
  Paper,
  Select,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";

import Alerts from "components/Alerts";
import Spinner from "components/Spinner/Spinner";
import MultiSelect from "pages/TargetForm/MultiSelect";
import RequiredLabelV2 from "pages/TargetForm/RequiredLabelV2";
import AlertDialog from "components/AlertDialog";
import {
  ConfirmationMessages,
  Constants,
  TitleDefinitionConstants,
} from "constants/constant";
import numericValidator from "services/numericValidator";
import {
  baseYearOptions,
  initialCategories,
  scopesCoveredOptions,
  targetYearsAfter,
} from "pages/TargetForm/data/TargetFormData";

import type {
  GHGScope3CategoryRow,
  GHGScope3Reduction,
  GHGTargetForm,
  GHGTargetGridRow,
  GHGTargetSetFormProps,
} from "./types";

// ── helpers ───────────────────────────────────────────────────────────────────

const buildInitialCategories = (): GHGScope3CategoryRow[] =>
  initialCategories.map((c) => ({
    nzdmId: c.nzdmId,
    id: 0,
    targetNumber: 0,
    catagory: c.catagory,
    catagoryName: c.catagoryName,
    reduction: "0",
    selected: false,
  }));

const isPositiveReduction = (v: number | string | null | undefined): boolean => {
  const n = parseFloat((v ?? "0").toString());
  return !Number.isNaN(n) && n > 0;
};

const getReducedCategories = (rows: GHGScope3CategoryRow[]): GHGScope3CategoryRow[] =>
  rows.filter((r) => isPositiveReduction(r.reduction));

const buildScope3Payload = (rows: GHGScope3CategoryRow[]): GHGScope3Reduction[] | null => {
  const reduced = getReducedCategories(rows);
  if (reduced.length === 0) return null;
  return reduced.map((r) => ({
    id: r.id,
    targetNumber: r.targetNumber,
    catagory: r.catagory,
    catagoryName: r.catagoryName,
    reduction: Number(r.reduction),
    createdBy: null,
    modifiedBy: null,
    createdAt: null,
    modifiedAt: null,
    isActive: null,
  }));
};

const defaultForm = (): GHGTargetForm => ({
  order: 0,
  targetNumber: 0,
  targetName: null,
  // A client or organisation target is independent of its locations, so it owns its Baseline Year.
  baseYear: null,
  targetYear: Constants.TargetYear,
  scopesCovered: null,
  baselineValue: null,
  targetedValue: null,
  reduction: null,
  scope1Reduction: null,
  scope2Reduction: null,
  scope3GHGReduction: null,
  createdBy: null,
  modifiedBy: null,
  createdAt: null,
  modifiedAt: null,
  isActive: null,
});

// ── component ─────────────────────────────────────────────────────────────────

const GHGTargetSetForm: React.FC<GHGTargetSetFormProps> = ({
  entityId,
  handleCloseSnackBar,
  testIds,
  api,
}) => {
  const targetYearRef = useRef<HTMLDivElement>(null);

  // Keep a stable ref to the latest api object so fetchGrid doesn't need to
  // list the entire api prop as a dependency (call sites pass inline objects).
  const apiRef = useRef(api);
  apiRef.current = api;

  const [loading, setLoading] = useState(false);
  const [gridData, setGridData] = useState<GHGTargetGridRow[]>([]);
  const [targetForm, setTargetForm] = useState<GHGTargetForm>(defaultForm());
  const [categories, setCategories] = useState<GHGScope3CategoryRow[]>(buildInitialCategories());
  const [selectedScopes, setSelectedScopes] = useState<number[]>([]);
  const [hasScope3, setHasScope3] = useState(false);
  const [checkboxChecked, setCheckboxChecked] = useState(false);
  const [bulkReductionValue, setBulkReductionValue] = useState("");
  const [showForm, setShowForm] = useState(false);

  // validation flags
  const [isScopesCoveredError, setIsScopesCoveredError] = useState(false);
  const [isScope3Error, setIsScope3Error] = useState(false);
  const [isYearError, setIsYearError] = useState(false);
  const [isTargetNameError, setIsTargetNameError] = useState(false);

  // delete dialog
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [pendingDeleteNumber, setPendingDeleteNumber] = useState(0);
  const [pendingDeleteName, setPendingDeleteName] = useState("");

  // ── data fetching ──────────────────────────────────────────────────────────

  const fetchGrid = useCallback(async () => {
    if (!entityId) return;
    try {
      setLoading(true);
      const res = await apiRef.current.fetchGrid(entityId);
      if (res) setGridData(res);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [entityId]);

  useEffect(() => {
    fetchGrid();
  }, [fetchGrid]);

  // The entity's target is anchored to its own Baseline Year. The locations' baseline years still come
  // back on the grid rows so a mismatch can be pointed out, but they never drive the form.
  const applicableBaselineYears = React.useMemo(() => {
    const years = gridData.flatMap((row) => row.applicableBaselineYears ?? []);
    return [...new Set(years)].sort((a, b) => a - b);
  }, [gridData]);

  const baselineYearLabel = applicableBaselineYears.join(", ");
  const validTargetYears = targetYearsAfter(targetForm.baseYear ?? undefined);
  const storedTargetYear = Number(targetForm.targetYear);
  const invalidStoredTargetYear =
    Number.isFinite(storedTargetYear) && !validTargetYears.includes(storedTargetYear)
      ? storedTargetYear
      : undefined;
  const displayedTargetYears =
    invalidStoredTargetYear == null
      ? validTargetYears
      : [...validTargetYears, invalidStoredTargetYear].sort((a, b) => a - b);
  const formBaseYearDiverges =
    applicableBaselineYears.length > 0 &&
    targetForm.baseYear != null &&
    !applicableBaselineYears.includes(Number(targetForm.baseYear));

  // ── form reset ─────────────────────────────────────────────────────────────

  const resetForm = () => {
    setTargetForm(defaultForm());
    setCategories(buildInitialCategories());
    setSelectedScopes([]);
    setHasScope3(false);
    setCheckboxChecked(false);
    setBulkReductionValue("");
    setIsScopesCoveredError(false);
    setIsScope3Error(false);
    setIsYearError(false);
    setIsTargetNameError(false);
    setShowForm(false);
  };

  // ── handlers ───────────────────────────────────────────────────────────────

  const handleTextChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setTargetForm((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleScopeNumericChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const validated = numericValidator.allowNumericValue(e.target.value.toString());
    const asNumber: number | null = validated === "" ? null : Number(validated);
    setTargetForm((prev) => ({ ...prev, [e.target.name]: asNumber }));
  };

  const handleSelectChange = (name: string, value: any) => {
    setTargetForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleScopeSelectionChange = (opts: number[]) => {
    const has3 = opts.includes(3);
    setHasScope3(has3);
    setTargetForm((prev) => ({
      ...prev,
      scopesCovered: opts.join(", ") || null,
      scope1Reduction: opts.includes(1) ? prev.scope1Reduction : null,
      scope2Reduction: opts.includes(2) ? prev.scope2Reduction : null,
      scope3GHGReduction: has3 ? prev.scope3GHGReduction : null,
    }));
    if (!has3) {
      setCategories((c) => c.map((row) => ({ ...row, reduction: "0", selected: false })));
      setCheckboxChecked(false);
      setBulkReductionValue("");
      setIsScope3Error(false);
    }
    setSelectedScopes(opts);
    setIsScopesCoveredError(false);
  };

  const handleReductionChange = (nzdmId: number, value: string) => {
    setCategories((prev) => {
      const updated = prev.map((c) => {
        if (c.nzdmId !== nzdmId) return c;
        const parsed = numericValidator.allowNumericValue(value);
        return { ...c, reduction: parsed, selected: isPositiveReduction(parsed) };
      });
      setTargetForm((tf) => ({ ...tf, scope3GHGReduction: buildScope3Payload(updated) }));
      return updated;
    });
  };

  const handleBulkCheckboxChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setCheckboxChecked(e.target.checked);
    if (e.target.checked) applyBulkReduction(bulkReductionValue);
    else resetScopeCategories();
  };

  const handleBulkValueChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setBulkReductionValue(e.target.value);
    if (checkboxChecked) applyBulkReduction(e.target.value);
  };

  const applyBulkReduction = (value: string) => {
    const parsed = numericValidator.allowNumericValue(value);
    const updated = categories.map((c) => ({
      ...c,
      reduction: parsed,
      selected: isPositiveReduction(parsed),
    }));
    setCategories(updated);
    setTargetForm((tf) => ({ ...tf, scope3GHGReduction: buildScope3Payload(updated) }));
  };

  const resetScopeCategories = () => {
    setCategories((c) => c.map((row) => ({ ...row, reduction: "0", selected: false })));
    setBulkReductionValue("");
    setTargetForm((tf) => ({ ...tf, scope3GHGReduction: null }));
  };

  // ── edit ──────────────────────────────────────────────────────────────────

  const handleEdit = async (targetNumber: number) => {
    try {
      setLoading(true);
      const res = await apiRef.current.getTarget(targetNumber);
      if (!res) return;
      setTargetForm(res);

      const scopes = res.scopesCovered
        ? res.scopesCovered.split(",").map((s) => parseInt(s.trim(), 10)).filter(Boolean)
        : [];
      setSelectedScopes(scopes);
      const has3 = scopes.includes(3);
      setHasScope3(has3);

      const rebuilt = buildInitialCategories().map((cat) => {
        const saved = res.scope3GHGReduction?.find((s) => s.catagory === cat.catagory);
        if (saved) {
          return {
            ...cat,
            id: saved.id,
            targetNumber: saved.targetNumber,
            reduction: saved.reduction ?? "0",
            selected: isPositiveReduction(saved.reduction),
          };
        }
        return cat;
      });
      setCategories(rebuilt);
      setShowForm(true);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  // ── delete ────────────────────────────────────────────────────────────────

  const handleDeleteClick = (targetNumber: number, targetName: string) => {
    setPendingDeleteNumber(targetNumber);
    setPendingDeleteName(targetName ?? "");
    setDeleteDialogOpen(true);
  };

  const handleConfirmDelete = async () => {
    setDeleteDialogOpen(false);
    try {
      await apiRef.current.deleteTarget(pendingDeleteNumber);
      await fetchGrid();
      handleCloseSnackBar({ show: true, severity: "success", message: "Deleted Successfully!" });
    } catch (err) {
      console.error(err);
    }
  };

  // ── save / update ─────────────────────────────────────────────────────────

  const handleSave = async () => {
    let valid = true;

    if (!targetForm.targetName?.trim()) {
      setIsTargetNameError(true);
      valid = false;
    } else {
      setIsTargetNameError(false);
    }

    if (!targetForm.scopesCovered) {
      setIsScopesCoveredError(true);
      valid = false;
    } else {
      setIsScopesCoveredError(false);
    }

    // The target owns both ends of its window, so they are validated against each other.
    if (
      targetForm.baseYear == null ||
      targetForm.targetYear == null ||
      targetForm.baseYear >= targetForm.targetYear
    ) {
      setIsYearError(true);
      valid = false;
    } else {
      setIsYearError(false);
    }

    if (
      targetForm.scopesCovered?.includes("3") &&
      (!targetForm.scope3GHGReduction || targetForm.scope3GHGReduction.length === 0)
    ) {
      setIsScope3Error(true);
      valid = false;
    } else {
      setIsScope3Error(false);
    }

    if (!valid) return;

    try {
      setLoading(true);
      const payload: GHGTargetForm = { ...targetForm, isActive: true };
      const isEdit = (payload.targetNumber ?? 0) > 0;
      if (isEdit) {
        await apiRef.current.updateTarget(payload, entityId);
      } else {
        await apiRef.current.addTarget(payload, entityId);
      }
      const message = isEdit
        ? "Target Updated Successfully!"
        : `${payload.targetName} Created Successfully!`;
      handleCloseSnackBar({ show: true, severity: "success", message });
      resetForm();
      await fetchGrid();
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const isEditing = (targetForm.targetNumber ?? 0) > 0;

  // ── render ────────────────────────────────────────────────────────────────

  return (
    <Box sx={{ mx: 1, my: 0.5 }}>

      {/* Target count + Add button */}
      <Grid container alignItems="center" sx={{ mb: 0.75 }}>
        <Grid item xs>
          <Typography sx={{ fontSize: "0.75rem", color: "text.secondary" }}>
            {gridData.length} target{gridData.length !== 1 ? "s" : ""} configured
          </Typography>
        </Grid>
        <Grid item>
          {!showForm && (
            <Button
              variant="contained"
              size="small"
              data-testid={testIds.addBtn}
              onClick={() => setShowForm(true)}
              sx={{ textTransform: "none", fontSize: "0.72rem", py: 0.25, px: 1.5 }}
            >
              Add Target
            </Button>
          )}
        </Grid>
      </Grid>

      {/* Targets grid */}
      {gridData.length > 0 ? (
        <Paper elevation={2} sx={{ overflowX: "auto", borderRadius: 1, mb: 1 }}>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ backgroundColor: "#f5f5f5" }}>
                {[
                  "Target Name",
                  "Baseline Year",
                  "Target Year",
                  "Scopes Covered",
                  "Scope 3 Categories",
                  "Baseline Value",
                  "% Reduction",
                  "Actions",
                ].map((h) => (
                  <TableCell
                    key={h}
                    sx={{ fontWeight: 600, fontSize: "0.7rem", py: 0.75, whiteSpace: "nowrap" }}
                  >
                    {h}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {gridData.map((row) => (
                <TableRow key={row.targetNumber} hover>
                  <TableCell sx={{ fontSize: "0.7rem", fontWeight: 600 }}>{row.targetName}</TableCell>
                  <TableCell sx={{ fontSize: "0.7rem" }}>
                    {row.baseYear}
                    {row.baseYearDivergesFromBaseline && (
                      <Tooltip
                        title={`Baseline Year ${row.baseYear} differs from the location baseline year${
                          (row.applicableBaselineYears ?? []).length > 1 ? "s" : ""
                        } ${(row.applicableBaselineYears ?? []).join(", ")}.`}
                      >
                        <Box
                          component="span"
                          data-testid={`baseYearDivergence-${row.targetNumber}`}
                          sx={{ color: "#b45a3f", ml: 0.5, cursor: "help" }}
                        >
                          *
                        </Box>
                      </Tooltip>
                    )}
                  </TableCell>
                  <TableCell sx={{ fontSize: "0.7rem" }}>{row.targetYear}</TableCell>
                  <TableCell sx={{ fontSize: "0.7rem" }}>{row.scopesCovered}</TableCell>
                  <TableCell sx={{ fontSize: "0.7rem" }}>
                    {row.scope3Categories ? (
                      <Tooltip title={row.scope3Categories}>
                        <span>
                          {row.scope3Categories.length > 25
                            ? row.scope3Categories.slice(0, 25) + "…"
                            : row.scope3Categories}
                        </span>
                      </Tooltip>
                    ) : (
                      "Not Applicable"
                    )}
                  </TableCell>
                  <TableCell sx={{ fontSize: "0.7rem" }}>
                    {row.baselineValue != null ? row.baselineValue : "—"}
                  </TableCell>
                  <TableCell sx={{ fontSize: "0.7rem" }}>
                    {row.reduction != null ? `${row.reduction}%` : "—"}
                  </TableCell>
                  <TableCell sx={{ py: 0.25 }}>
                    <Tooltip title="Edit Target">
                      <IconButton
                        size="small"
                        aria-label="Edit Target"
                        onClick={() => handleEdit(row.targetNumber)}
                        sx={{ p: 0.25 }}
                      >
                        <EditIcon sx={{ color: "#e4610f", fontSize: 15 }} />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Delete Target">
                      <IconButton
                        size="small"
                        aria-label="Delete Target"
                        onClick={() => handleDeleteClick(row.targetNumber, row.targetName ?? "")}
                        sx={{ p: 0.25 }}
                      >
                        <DeleteOutlineIcon sx={{ color: "#e4610f", fontSize: 15 }} />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Paper>
      ) : (
        !showForm && (
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ textAlign: "center", py: 1, fontSize: "0.75rem" }}
          >
            No targets configured yet.
          </Typography>
        )
      )}

      <Typography
        variant="caption"
        sx={{ color: "text.secondary", display: "block", mb: 0.5, fontStyle: "italic" }}
      >
        Targets are used for GHG reduction tracking across all locations.
      </Typography>

      {/* Add / Edit form */}
      {showForm && (
        <Paper variant="outlined" sx={{ p: 1.5, borderRadius: 1, mt: 0.5 }}>

          {/* Validation alerts */}
          {(isTargetNameError || isScopesCoveredError || isYearError || isScope3Error) && (
            <Box sx={{ mb: 0.5 }}>
              {isTargetNameError && (
                <Alerts severity="error" message="Target Name is required" show />
              )}
              {isScopesCoveredError && (
                <Alerts
                  severity="error"
                  message="At least one must be selected from GHG Scopes Covered"
                  show
                />
              )}
              {isYearError && (
                <Alerts
                  severity="error"
                  message="Target year must be greater than Baseline Year"
                  show
                />
              )}
              {isScope3Error && (
                <Alerts
                  severity="error"
                  message="At least one category with a reduction must be set for Scope 3"
                  show
                />
              )}
            </Box>
          )}

          {/* Form fields */}
          <Grid container spacing={1}>
            {/* Target Name */}
            <Grid item xs={6}>
              <RequiredLabelV2 labelText="Target Name" isHelperTextRequired={false} />
              <TextField
                name="targetName"
                value={targetForm.targetName ?? ""}
                size="small"
                fullWidth
                placeholder="Enter target name"
                onChange={(e) => { setIsTargetNameError(false); handleTextChange(e); }}
                error={isTargetNameError}
                inputProps={{
                  "data-testid": testIds.targetName,
                  style: { fontSize: 11, padding: "4px 6px" },
                }}
                sx={{ mt: 0.25, "& .MuiInputBase-root": { height: 26, fontSize: 11 } }}
              />
            </Grid>

            {/* Baseline Year */}
            <Grid item xs={6}>
              <RequiredLabelV2
                labelText="Baseline Year"
                isHelperTextRequired
                content={TitleDefinitionConstants.BaseYear}
              />
              {/* A client or organisation target is independent of its locations: it sets its own
                  start year rather than inheriting theirs. */}
              <FormControl fullWidth size="small" sx={{ mt: 0.25 }}>
                <Select
                  name="baseYear"
                  value={targetForm.baseYear ?? ""}
                  onChange={(e) => {
                    setIsYearError(false);
                    handleSelectChange("baseYear", e.target.value);
                  }}
                  displayEmpty
                  error={isYearError}
                  size="small"
                  fullWidth
                  inputProps={{ "data-testid": "baseYear" }}
                  SelectDisplayProps={{ "data-testid": "baseYear-select" } as any}
                  sx={{ height: 26, fontSize: 11, ".MuiSelect-select": { py: "4px", fontSize: 11 } }}
                  MenuProps={{ PaperProps: { sx: { fontSize: 11 } } }}
                >
                  <MenuItem value="" sx={{ fontSize: 11 }}>Select Baseline Year</MenuItem>
                  {baseYearOptions.map((y) => (
                    <MenuItem key={y} value={y} sx={{ fontSize: 11 }}>{y}</MenuItem>
                  ))}
                </Select>
              </FormControl>
              {formBaseYearDiverges && (
                <Typography
                  data-testid="baseYearDivergence"
                  sx={{ fontSize: 10, color: "#b45a3f", mt: 0.25 }}
                >
                  Baseline Year {targetForm.baseYear} differs from the location baseline year
                  {applicableBaselineYears.length > 1 ? "s" : ""} {baselineYearLabel}.
                </Typography>
              )}
            </Grid>

            {/* Target Year */}
            <Grid item xs={6}>
              <RequiredLabelV2
                labelText="Target Year"
                isHelperTextRequired
                content={TitleDefinitionConstants.TargetYear}
              />
              <FormControl fullWidth size="small" ref={targetYearRef} sx={{ mt: 0.25 }}>
                <Select
                  name="targetYear"
                  value={targetForm.targetYear ?? ""}
                  onChange={(e) => handleSelectChange("targetYear", e.target.value)}
                  displayEmpty
                  size="small"
                  fullWidth
                  sx={{ height: 26, fontSize: 11, ".MuiSelect-select": { py: "4px", fontSize: 11 } }}
                  MenuProps={{ PaperProps: { sx: { fontSize: 11 } } }}
                >
                  <MenuItem value="" sx={{ fontSize: 11 }}>Select Target Year</MenuItem>
                  {displayedTargetYears.map((y) => (
                    <MenuItem
                      key={y}
                      value={y}
                      disabled={y === invalidStoredTargetYear}
                      sx={{ fontSize: 11 }}
                    >
                      {y}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            {/* GHG Scopes Covered */}
            <Grid item xs={6}>
              <RequiredLabelV2
                labelText="GHG Scopes Covered"
                isHelperTextRequired
                content={TitleDefinitionConstants.ScopesCovered}
              />
              <Box sx={{ mt: 0.25 }}>
                <MultiSelect
                  options={scopesCoveredOptions}
                  onSelectionChange={handleScopeSelectionChange}
                  selectedOptions={selectedScopes}
                  sx={{
                    height: 26,
                    minHeight: 26,
                    fontSize: 11,
                    mt: 0,
                    ".MuiSelect-select": {
                      fontSize: 11,
                      height: 26,
                      paddingTop: "4px",
                      paddingBottom: "4px",
                    },
                  }}
                />
              </Box>
            </Grid>

            {/* Scope 1 */}
            <Grid item xs={6}>
              <RequiredLabelV2
                labelText="Scope 1% Reduction"
                isHelperTextRequired
                content={TitleDefinitionConstants.ScopeReduction}
              />
              <TextField
                name="scope1Reduction"
                value={selectedScopes.includes(1) ? (targetForm.scope1Reduction ?? "") : "0"}
                size="small"
                fullWidth
                disabled={!selectedScopes.includes(1)}
                onChange={handleScopeNumericChange}
                inputProps={{
                  inputMode: "numeric",
                  maxLength: 5,
                  "data-testid": testIds.scope1,
                  style: { fontSize: 11, padding: "4px 6px" },
                }}
                sx={{ mt: 0.25, "& .MuiInputBase-root": { height: 26, fontSize: 11 } }}
              />
            </Grid>

            {/* Scope 2 */}
            <Grid item xs={6}>
              <RequiredLabelV2
                labelText="Scope 2% Reduction"
                isHelperTextRequired
                content={TitleDefinitionConstants.ScopeReduction}
              />
              <TextField
                name="scope2Reduction"
                value={selectedScopes.includes(2) ? (targetForm.scope2Reduction ?? "") : "0"}
                size="small"
                fullWidth
                disabled={!selectedScopes.includes(2)}
                onChange={handleScopeNumericChange}
                inputProps={{
                  inputMode: "numeric",
                  maxLength: 5,
                  "data-testid": testIds.scope2,
                  style: { fontSize: 11, padding: "4px 6px" },
                }}
                sx={{ mt: 0.25, "& .MuiInputBase-root": { height: 26, fontSize: 11 } }}
              />
            </Grid>
          </Grid>

          {/* Note */}
          <Box sx={{ mt: 0.5, mb: 0.5 }}>
            <Typography
              sx={{ fontSize: "0.62rem", color: "text.secondary", lineHeight: 1.3, fontStyle: "italic" }}
            >
              <strong>Note:</strong> {TitleDefinitionConstants.ScopeReductionNote}
            </Typography>
          </Box>

          {/* Scope 3 categories */}
          {hasScope3 && (
            <>
              <Divider sx={{ my: 0.75 }} />
              <Grid container spacing={0.5}>
                <Grid item xs={12}>
                  <Typography sx={{ fontWeight: "bold", color: "#718096", fontSize: 12, mb: 0.5 }}>
                    Baseline Scope 3 GHG Emissions
                  </Typography>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={checkboxChecked}
                          onChange={handleBulkCheckboxChange}
                          data-testid={testIds.allCategory}
                          sx={{ p: 0.25 }}
                        />
                      }
                      label={<span style={{ fontSize: 11 }}>Apply to all categories</span>}
                      sx={{ ml: 0, mr: 0 }}
                    />
                    <TextField
                      value={bulkReductionValue}
                      onChange={handleBulkValueChange}
                      size="small"
                      disabled={!checkboxChecked}
                      placeholder="Enter %"
                      inputProps={{
                        "data-testid": testIds.reductionPct,
                        style: { fontSize: 11, padding: "3px 6px" },
                      }}
                      sx={{ width: 120, "& .MuiInputBase-root": { height: 24, fontSize: 11 } }}
                    />
                  </Box>
                </Grid>
                <Grid item xs={12}>
                  <Table size="small" sx={{ mt: 0.5 }}>
                    <TableHead>
                      <TableRow sx={{ backgroundColor: "#f9f9f9" }}>
                        <TableCell sx={{ fontWeight: 600, fontSize: "0.68rem", py: 0.5, width: 60 }}>
                          Category
                        </TableCell>
                        <TableCell sx={{ fontWeight: 600, fontSize: "0.68rem", py: 0.5 }}>
                          Category Name
                        </TableCell>
                        <TableCell sx={{ fontWeight: 600, fontSize: "0.68rem", py: 0.5, width: 130 }}>
                          % Reduction
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {categories.map((row) => (
                        <TableRow key={row.nzdmId} hover>
                          <TableCell sx={{ fontSize: "0.68rem", py: 0.25 }}>{row.catagory}</TableCell>
                          <TableCell sx={{ fontSize: "0.68rem", py: 0.25 }}>{row.catagoryName}</TableCell>
                          <TableCell sx={{ py: 0.25 }}>
                            <TextField
                              value={row.reduction ?? 0}
                              size="small"
                              variant="outlined"
                              placeholder="%"
                              inputProps={{
                                inputMode: "numeric",
                                maxLength: 5,
                                style: { fontSize: 11, padding: "2px 6px" },
                              }}
                              onChange={(e) => handleReductionChange(row.nzdmId, e.target.value)}
                              sx={{ width: 90, "& .MuiInputBase-root": { height: 22, fontSize: 11 } }}
                            />
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </Grid>
              </Grid>
            </>
          )}

          <Divider sx={{ my: 1 }} />

          {/* Save / Cancel */}
          <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1, mb: 0.75 }}>
            <Button
              size="small"
              variant="outlined"
              onClick={resetForm}
              sx={{ textTransform: "none", fontSize: "0.72rem", py: 0.25, px: 1.5 }}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              size="small"
              data-testid={testIds.saveBtn}
              onClick={handleSave}
              sx={{ textTransform: "none", fontSize: "0.72rem", py: 0.25, px: 1.5 }}
            >
              {isEditing ? "Update Target" : "Save Target"}
            </Button>
          </Box>
        </Paper>
      )}

      {/* Delete confirmation dialog */}
      <AlertDialog
        open={deleteDialogOpen}
        openDialog={() => setDeleteDialogOpen(true)}
        handleClose={() => setDeleteDialogOpen(false)}
        handleConfirm={handleConfirmDelete}
        locationName={pendingDeleteName}
        confirmationMessage={ConfirmationMessages.ghgConfirmation}
      />

      {loading && <Spinner size={80} data-testid="spinner" />}
    </Box>
  );
};

export default GHGTargetSetForm;
